/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import a11yKeyPress from 'js/lib/a11yKeyPress';
import { useRetracked } from 'js/lib/retracked';
import useRouter from 'js/lib/useRouter';

import type { Theme } from '@coursera/cds-core';
import { Tab, TabContext, TabPanel, Tabs, Typography2, breakpoints } from '@coursera/cds-core';
import { CloseIcon, MenuIcon } from '@coursera/cds-icons';

import {
  CourseVideosAccordionGroup,
  CourseVideosAccordionGroupLoading,
} from 'bundles/clips/components/CourseVideosAccordionGroup';
import RelatedVideosList from 'bundles/clips/components/RelatedVideosList';
import { RelatedVideosSearchWrapper } from 'bundles/clips/components/RelatedVideosSearchWrapper';
import type { Course, VLPItem, VLPLesson } from 'bundles/clips/types/vlpSharedTypes';

import _t from 'i18n!nls/clips';

type PropsFromCaller = {
  course: Course;
  lectureItems: Array<VLPItem>;
  currentVideoItem: VLPItem;
  lessons: Array<VLPLesson>;
  currentLesson?: VLPLesson;
  skills: Array<string>;
  programId?: string;
  isLoading: boolean;
};

export type Props = PropsFromCaller;

const TabNames = {
  CourseVideos: 'COURSE_VIDEOS',
  RelatedVideos: 'RELATED_VIDEOS',
} as const;

type TabName = (typeof TabNames)[keyof typeof TabNames];

export const navBarContainer = (theme: Theme) =>
  css({
    width: '350px',
    border: `1px solid var(--cds-color-grey-50)`,
    [breakpoints.down('xs')]: {
      width: 'auto',
      marginLeft: '-16px',
      marginRight: '-16px',
      border: 'none',
      borderRadius: '8px',
      paddingeft: '4px',
    },
  });

const navBarContainerMobileHidden = () => css`
  ${breakpoints.down('xs')} {
    display: none;
  }
`;

const tabsContainer = (theme: Theme) =>
  css({
    padding: '0 20px',
    [breakpoints.down('md')]: {
      border: `1px solid var(--cds-color-grey-50)`,
    },
  });

const relatedVideosTab = (shouldShow?: boolean) =>
  css({
    display: shouldShow ? 'initial' : 'none',
  });

const tabPanel = (theme: Theme) =>
  css({
    overflow: 'auto',
    height: '900px',
    [breakpoints.down('md')]: {
      height: '500px',
    },
    [breakpoints.down('xs')]: {
      height: 'auto',
    },
  });

const mobileCourseHeader = () => css`
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  background: var(--cds-color-grey-975);
  margin-left: -16px;
  margin-right: -16px;
  padding: 16px;
  display: none;

  ${breakpoints.down('xs')} {
    display: flex;
  }
`;

const ClipsNavBarLoading = ({ currentTab, currentQuery, onTabChange }: ClipsNavBarLoadingProps) => {
  return (
    <div css={navBarContainer} data-testid="clips-navbar-loading">
      <TabContext value={currentTab}>
        <Tabs
          aria-label={_t('Course videos and its related videos')}
          onChange={onTabChange}
          variant="primary"
          css={tabsContainer}
        >
          <Tab
            label={_t('Course Videos')}
            value={TabNames.CourseVideos}
            data-testid="course-videos-tab"
            aria-aria-disabled
          />
          <Tab
            label={_t('Related Videos')}
            value={TabNames.RelatedVideos}
            data-testid="related-videos-tab"
            css={relatedVideosTab(Boolean(currentQuery))}
          />
        </Tabs>

        <TabPanel value={TabNames.CourseVideos} data-testid="course-videos-tabpanel">
          <CourseVideosAccordionGroupLoading />
        </TabPanel>

        <TabPanel value={TabNames.RelatedVideos} data-testid="related-videos-tabpanel">
          <CourseVideosAccordionGroupLoading />
        </TabPanel>
      </TabContext>
    </div>
  );
};

export const ClipsNavBar = ({
  course,
  lectureItems,
  currentVideoItem,
  lessons,
  currentLesson,
  skills,
  programId,
  isLoading,
}: Props) => {
  const router = useRouter();
  const {
    location: { query },
  } = router;

  const [showMobileNavbar, setShowMobileNavbar] = useState<boolean>(false);
  const onToggleMobileNavbar = () => setShowMobileNavbar((v) => !v);

  const { query: currentQuery } = query;

  const [currentTab, setCurrentTab] = useState<TabName>(
    course?.isStandaloneCourse ? TabNames.RelatedVideos : TabNames.CourseVideos
  );
  const trackEvent = useRetracked();
  const isTabName = (s: string): s is TabName => s === TabNames.CourseVideos || s === TabNames.RelatedVideos;

  const handleTabChange = (newValue: string) => {
    if (isTabName(newValue)) {
      setCurrentTab(newValue);
      trackEvent({ trackingData: {}, trackingName: `${newValue.toLowerCase()}_tab`, action: 'click' });
    }
  };

  if (isLoading) {
    return <ClipsNavBarLoading currentTab={currentTab} currentQuery={currentQuery} onTabChange={handleTabChange} />;
  }
  const isStandaloneCourse = course?.isStandaloneCourse;
  if (isStandaloneCourse && !currentQuery) {
    return null;
  }

  return (
    <RelatedVideosSearchWrapper skills={skills} programId={programId}>
      <div css={mobileCourseHeader}>
        <Typography2 component="p" color="invertBody">
          {currentLesson?.name}
        </Typography2>
        <div
          role="button"
          onClick={onToggleMobileNavbar}
          onKeyPress={(event) => a11yKeyPress(event, onToggleMobileNavbar)}
          aria-label={_t('Toggle mobile navigation')}
          data-testid="mobile-navbar-toggle-btn"
        >
          {showMobileNavbar ? <CloseIcon color="invert" /> : <MenuIcon color="invert" />}
        </div>
      </div>
      <div css={[navBarContainer, showMobileNavbar ? {} : navBarContainerMobileHidden]} data-testid="clips-navbar">
        <TabContext value={currentTab}>
          <Tabs
            aria-label={_t('Course videos and its related videos')}
            onChange={handleTabChange}
            variant="primary"
            css={tabsContainer}
          >
            <Tab
              label={_t('Course Videos')}
              value={TabNames.CourseVideos}
              data-testid="course-videos-tab"
              css={{ display: isStandaloneCourse ? 'none' : 'initial' }}
            />
            <Tab
              label={_t('Related Videos')}
              value={TabNames.RelatedVideos}
              data-testid="related-videos-tab"
              css={relatedVideosTab(Boolean(currentQuery))}
            />
          </Tabs>

          {!isStandaloneCourse && (
            <TabPanel value={TabNames.CourseVideos} data-testid="course-videos-tabpanel" css={tabPanel}>
              <CourseVideosAccordionGroup
                currentVideoItem={currentVideoItem}
                currentLesson={currentLesson}
                lessons={lessons}
                lectureItems={lectureItems}
                courseSlug={course.slug}
              />
            </TabPanel>
          )}

          <TabPanel value={TabNames.RelatedVideos} data-testid="related-videos-tabpanel" css={tabPanel}>
            <RelatedVideosList currentVideoItem={currentVideoItem} currentCourseId={course.id} />
          </TabPanel>
        </TabContext>
      </div>
    </RelatedVideosSearchWrapper>
  );
};

type ClipsNavBarLoadingProps = {
  currentTab: TabName;
  currentQuery: string;
  onTabChange: (s: string) => void;
};
